import * as React from 'react';
import * as classnames from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { InventoryStatusMessageTheme } from '../../../theme/default-theme';
import { Body2 } from 'elc-typography';
import { ContentSpacing } from 'elc-base-theme';
import { ITranslationsCollection, translate } from 'elc-service';
import { InventoryStatus } from '../../constants/InventoryStatuses';
import { enableByInventoryStatus, IWaitlist } from '../../../exported/product-cta/ProductCTA';

export interface IInventoryStatusMessageConfig {
    showInventoryMessaging?: string[];
    enableNotifyMe?: boolean;
    waitlist?: IWaitlist;
}
export interface IInventoryStatusMessage {
    config: IInventoryStatusMessageConfig;
    translations?: ITranslationsCollection;
    inventoryStatus: string;
    className?: string;
}

const StyledMessageBlock = styled.div`
    margin: ${ContentSpacing.space24} 0;
`;

const StyledMessageBody = styled(Body2)`
    ${InventoryStatusMessageTheme};
`;

export const InventoryStatusMessage = translate([
    'tempOutOfStockLabel',
    'itemIsTempOutOfStock',
    'itemIsTempOutOfStockNotifyMe',
    'itemIsComingSoon',
    'itemIsComingSoonNotifyMe',
    'itemIsSoldOut'
])(
    observer((props: IInventoryStatusMessage) => {
        const inventoryStatusMessageClassNames = classnames(
            'elc-add-to-bag-sku-inventory-status-message',
            'js-add-to-bag-sku-inventory-status-message',
            props.className
        );
        const inventoryStatusMessageMessageClassNames = classnames(
            'elc-add-to-bag-sku-inventory-status-message-message',
            'js-add-to-bag-sku-inventory-status-message-message'
        );

        const {
            tempOutOfStockLabel,
            itemIsTempOutOfStock,
            itemIsTempOutOfStockNotifyMe,
            itemIsComingSoon,
            itemIsComingSoonNotifyMe,
            itemIsSoldOut
        } = props.translations as ITranslationsCollection;
        const { config, inventoryStatus } = props;
        const { enableNotifyMe, waitlist, showInventoryMessaging } = config;

        const isProductAvailable = inventoryStatus === InventoryStatus.Active;
        const waitlistEnabledByStatus =
            waitlist && waitlist.enabledByStatus ? waitlist.enabledByStatus : [];
        const notifyMeEnabled = enableNotifyMe || (waitlist && waitlist.enabled);
        const isNotifyMeDisplayed =
            notifyMeEnabled &&
            !isProductAvailable &&
            enableByInventoryStatus(waitlistEnabledByStatus, inventoryStatus);

        // backwards compatible check - old translation field will be used until new one is pushed & populated
        // @todo: remove old translation field - https://jira.esteeonline.com/browse/MPPFIND-3361
        const tempOutOfStockMessage = itemIsTempOutOfStock
            ? itemIsTempOutOfStock
            : tempOutOfStockLabel;

        const tempOutOfStockMessageMapping = isNotifyMeDisplayed
            ? itemIsTempOutOfStockNotifyMe
            : tempOutOfStockMessage;
        const comingSoonMessageMapping = isNotifyMeDisplayed
            ? itemIsComingSoonNotifyMe
            : itemIsComingSoon;

        const messageMapping: { [k: string]: string | null } = {
            [InventoryStatus.TempOutOfStock]:
                showInventoryMessaging &&
                showInventoryMessaging.includes(InventoryStatus.TempOutOfStock)
                    ? tempOutOfStockMessageMapping
                    : null,
            [InventoryStatus.ComingSoon]:
                showInventoryMessaging &&
                showInventoryMessaging.includes(InventoryStatus.ComingSoon)
                    ? comingSoonMessageMapping
                    : null,
            [InventoryStatus.SoldOut]:
                showInventoryMessaging && showInventoryMessaging.includes(InventoryStatus.SoldOut)
                    ? itemIsSoldOut
                    : null
        };

        const message = messageMapping[inventoryStatus];

        if (message) {
            return (
                <StyledMessageBlock className={inventoryStatusMessageClassNames}>
                    <StyledMessageBody className={inventoryStatusMessageMessageClassNames}>
                        {message}
                    </StyledMessageBody>
                </StyledMessageBlock>
            );
        } else {
            return null;
        }
    })
);
